import { useContext, useCallback } from 'react';
import _get from 'lodash/get';
import _empty from 'lodash/isEmpty';
import { formatListData, AjaxContext, AppContext, EMPTY_ARRAY, UserContext, ERROR_SEVERITY } from 'commons-ui';
import { DowntimeRefundRequestRecord } from './listRefundRequest/types';
import { EDowntimeRefundURL, EResponseCode } from './helper';

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName }
	} = useContext(AppContext);
	const { user } = useContext(UserContext);
	const getErrorMessage = (error: any): string => {
		try {
			let firstIndex: number = error.response.data.error.indexOf('-');
			const errorObject = JSON.parse(error.response.data.error.slice(firstIndex + 1).trim()).error;
			firstIndex = errorObject.indexOf('-');
			const errorMessage: string = JSON.stringify(JSON.parse(errorObject.slice(firstIndex + 1).trim())?.data);
			return errorMessage;
		} catch (e: any) {
			return 'Something went wrong';
		}
	};

	const getRefundRequests = useCallback(
		async ({
			pageDetails
		}: {
			pageDetails: { pageNo: number; pageSize: number };
		}): Promise<{
			rowCount: number;
			data: DowntimeRefundRequestRecord[];
		}> => {
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const result = await postData({
				endpoint: '/marketing/dashboard/downtimeRefund/listRefundRequest',
				queryParams: {
					skip: skipCount,
					appName
					// searchText
				},
				body: {
					pageNumber: pageNo,
					limit: pageSize
				}
			});
			return {
				rowCount: _get(result.data, ['data', 'count'], 0),
				data: formatListData(_get(result.data, ['data', 'data'], EMPTY_ARRAY))
			};
		},
		EMPTY_ARRAY
	);

	const getRefundRequestStatus = useCallback(
		async (
			submittedData: DowntimeRefundRequestRecord,
			showAlert: any
		): Promise<{
			status: string;
			csvUrl: string;
		}> => {
			try {
				const result = await postData({
					endpoint: EDowntimeRefundURL.GET_REFUND_REQUEST_STATUS,
					queryParams: {
						appName
					},
					body: {
						recordId: submittedData.bulkRefundRecordId
					}
				});
				if (result.status !== EResponseCode.SUCCESS) {
					showAlert(result.data.message || 'Error occurred while downloading request', { type: ERROR_SEVERITY.ERROR });
					return { status: '', csvUrl: '' };
				}
				return {
					status: _get(result.data.data, ['data', 'status'], ''),
					csvUrl: _get(result.data.data, ['data', 'csvUrl'], '')
				};
			} catch (error: any) {
				showAlert(getErrorMessage(error) || 'Error occurred while downloading request', { type: ERROR_SEVERITY.ERROR });
				return { status: '', csvUrl: '' };
			}
		},
		EMPTY_ARRAY
	);

	const approveRefundRequest = useCallback(async (submittedData: DowntimeRefundRequestRecord, showAlert: any): Promise<void> => {
		try {
			const result = await postData({
				endpoint: EDowntimeRefundURL.APPROVE_AND_PROCESS_REFUND_REQUEST,
				queryParams: {
					appName
				},
				body: {
					recordId: submittedData._id,
					approverName: user?.userName,
					approverId: user?.email,
					gameName: submittedData.gameName,
					startTime: submittedData.startTime,
					endTime: submittedData.endTime,
					csvPath: submittedData.csvPath
				}
			});
			if (result.status !== EResponseCode.SUCCESS) {
				showAlert(result.data.message || 'Error occurred while approving request', { type: ERROR_SEVERITY.ERROR });
				return;
			}
			showAlert('Approved successfully');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while approving request', { type: ERROR_SEVERITY.ERROR });
		}
	}, EMPTY_ARRAY);

	const rejectRefundRequest = useCallback(async (submittedData: DowntimeRefundRequestRecord, showAlert: any): Promise<void> => {
		try {
			const result = await postData({
				endpoint: EDowntimeRefundURL.REJECT_REFUND_REQUEST,
				queryParams: {
					appName
				},
				body: {
					recordId: submittedData._id,
					approverName: user?.userName,
					approverId: user?.email
				}
			});
			if (result.status !== EResponseCode.SUCCESS) {
				showAlert(result.data.message || 'Error occurred while rejecting request', { type: ERROR_SEVERITY.ERROR });
				return;
			}
			showAlert('Rejected successfully');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while rejecting request', { type: ERROR_SEVERITY.ERROR });
		}
	}, EMPTY_ARRAY);

	const downloadRefundRequest = useCallback(async (submittedData: DowntimeRefundRequestRecord, showAlert: any): Promise<void> => {
		try {
			// eslint-disable-next-line
			console.log('submitted data', submittedData);
			const result = await postData({
				endpoint: EDowntimeRefundURL.DOWNLOAD_REFUND_REQUEST,
				queryParams: {
					appName
				},
				body: {
					path: submittedData.csvPath
				}
			});
			if (result.status !== EResponseCode.SUCCESS) {
				showAlert(result.data.message || 'Error occurred while downloading request', { type: ERROR_SEVERITY.ERROR });
				return;
			}
			window.open(result.data.data, '_self');
			showAlert('File downloaded successfully!');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while downloading request', { type: ERROR_SEVERITY.ERROR });
		}
	}, EMPTY_ARRAY);

	const downloadRefundRequestResult = useCallback(async (url: string, showAlert: any): Promise<void> => {
		try {
			if (!_empty(url)) {
				window.open(url, '_self');
				showAlert('File downloaded successfully!');
			}
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while downloading request', { type: ERROR_SEVERITY.ERROR });
		}
	}, EMPTY_ARRAY);

	const createRefundRequest = useCallback(
		async (gameName: string, downtimeType: string, startTime: number, endTime: number, showAlert: any): Promise<boolean> => {
			try {
				const response = await postData({
					endpoint: EDowntimeRefundURL.CREATE_REFUND_REQUEST,
					queryParams: {
						appName
					},
					body: {
						gameName,
						downtimeType,
						startTime,
						endTime
					}
				});
				if (response.status !== EResponseCode.SUCCESS) {
					showAlert('Error occurred while creating request', { type: ERROR_SEVERITY.ERROR });
					return false;
				}
				showAlert('created successfully');
			} catch (error: any) {
				showAlert(getErrorMessage(error) || 'Error occurred while creating request', { type: ERROR_SEVERITY.ERROR });
				return false;
			}
			return true;
		},
		EMPTY_ARRAY
	);

	return {
		getRefundRequests,
		createRefundRequest,
		approveRefundRequest,
		rejectRefundRequest,
		downloadRefundRequest,
		getRefundRequestStatus,
		downloadRefundRequestResult
	};
};

export default useService;
